import * as React from "react"
import { v4 as uuidv4 } from "uuid"
import { Flex, Box, Avatar as CommonAvatar, Button } from "@chakra-ui/react"
import { Avatar } from "./Avatar"
import { convertСonfigToNumber, convertСonfig } from "./utils"
import { AvatarEditor } from "./AvatarEditor"
import { useRecoilState, useRecoilValue } from "recoil"
import { AvatarList } from "./AvatarList"
import { useSetRecoilState } from "recoil"
import { IoClose } from "react-icons/io5"
import { Icon } from "../../../common/icons"
import {
  configAvatarAtom,
  avatarListConfigsSelector,
  saveConfigAvatarAtom
} from "../../../../recoil/atoms/avatar"
import { useLocalStorage } from "../../../../hooks/useLocalStorage"
import { brandingSelector } from "../../../../recoil/atoms/room"
import { getUserTypeByUrl } from "../../../../util/getUserTypeByUrl"
import { UserType } from "../../../../types/user"
import { Logo } from "../../../header/logo"
import layoutStates from "../../../../recoil/atoms/layout"
import { useTranslation } from "react-i18next"
import { CURRENT_URL } from "../../../../constants"

const _AvatarsModal = ({
  closeModal,
  height,
  visible,
  isAudienceType,
  isMobile,
  isLandscape,
  isPortraitMobileAvatarView
}: {
  closeModal: () => void
  height?: number
  visible: boolean
  isAudienceType: boolean
  isMobile: boolean
  isLandscape?: boolean
  isPortraitMobileAvatarView: boolean
}) => {
  const { t } = useTranslation()
  const [config, setConfig] = useRecoilState(configAvatarAtom)
  const [saveConfig, setSaveConfig] = useRecoilState(saveConfigAvatarAtom)
  const setAvatarConfigList = useSetRecoilState(avatarListConfigsSelector)
  const [showHeader] = useRecoilState(layoutStates.showHeader)
  const [, , setLocalStorage, removeStorage] =
    useLocalStorage("localUserAavatar")
  const userTypeFromUrl = getUserTypeByUrl()
  const isModeratorType = React.useMemo(
    () => userTypeFromUrl === UserType.MODERATOR,
    [userTypeFromUrl]
  )
  const roomStyles = useRecoilValue(brandingSelector)
  const profileImageUrl =
    isModeratorType && roomStyles.roomAvatarImage
      ? roomStyles.roomAvatarImage
      : ""

  React.useEffect(() => {
    if (saveConfig !== "") {
      const avatar = convertСonfig(saveConfig)
      setConfig(avatar)
    }
  }, [saveConfig, setConfig])

  const selectConfig = config => {
    setConfig(config)
  }

  const updateConfig = React.useCallback(
    (key, value) => {
      let object = { ...config }
      object[key] = value
      setConfig(object)
      setAvatarConfigList({ type: "UPDATE", payload: object })
    },
    [config, setAvatarConfigList, setConfig]
  )

  const handleSaveAvatar = React.useCallback(() => {
    const data = convertСonfigToNumber(config)

    setSaveConfig(data)
    setLocalStorage(
      {
        [`avatar-${uuidv4()}`]: {
          avatarConfig: data,
          url: CURRENT_URL
        }
      },
      "rewrite"
    )
  }, [config, setLocalStorage, setSaveConfig])

  const handleCancelAvatar = React.useCallback((): void => {
    setConfig(null)
    setSaveConfig("")
    removeStorage()
  }, [removeStorage, setConfig, setSaveConfig])

  const avatarStyle: React.CSSProperties = React.useMemo(() => {
    return {
      width: isLandscape ? "75px" : isMobile ? "100px" : "200px",
      height: isLandscape ? "75px" : isMobile ? "100px" : "200px"
    }
  }, [isLandscape, isMobile])

  const handleCloseModal = React.useCallback((): void => {
    if (config !== null) {
      handleSaveAvatar()
    }
    closeModal()
    setAvatarConfigList({ type: "DELETE", payload: "" })
  }, [closeModal, config, handleSaveAvatar, setAvatarConfigList])

  return (
    <Flex
      width="100%"
      flexDirection="column"
      alignItems="center"
      position="relative"
      // height={height && height}
      /* background={roomStyles?.joinPagePanelBackgroundColor ?? "white"} */
      visibility={isAudienceType ? (visible ? "visible" : "hidden") : "unset"}
      className="avatar-modal"
    >
      {/* {!isPortraitMobileAvatarView && ( */}
        <Flex
          alignItems="center"
          justifyContent="center"
          width="2rem"
          minH="2rem"
          height="2rem"
          pos={isMobile ? "relative" : "absolute"}
          marginLeft={isMobile && "auto"}
          top={"0px"}
          right={"0px"}
          bg={roomStyles?.joinButtonBackgroundColor ?? "#1AC541"}
          textColor={roomStyles?.joinPageButtonTextColor ?? "#FFFFFF"}
          _hover={{
            backgroundColor:
              roomStyles?.joinButtonMouseoverBackgroundColor ?? "#24A840",
            textColor: roomStyles?.joinPageButtonMouseoverTextColor ?? "#FFFFFF"
          }}
          borderRadius="50%"
          onClick={() => {
            closeModal()
            setAvatarConfigList({ type: "DELETE", payload: "" })
          }}
          cursor="pointer"
        >
          <Icon src={IoClose} fontSize="1.3rem" />
        </Flex>
      {/* )} */}
      {((isLandscape && !showHeader) || (isMobile && !showHeader && isAudienceType)) && (
        <Flex alignItems="center" justifyContent="center">
          <Logo src={roomStyles?.roomHeaderIcon} isLandscape={isLandscape} />
        </Flex>
      )}

      <Flex flexDirection="column" alignItems="center" height="100%">
        <Box
          id={"myAvatar"}
          width={isLandscape ? "75px" : isMobile ? "100px" : "200px"}
          height={isLandscape ? "75px" : isMobile ? "100px" : "200px"}
          marginBottom={isLandscape ? "0.4rem" : "1rem"}
          marginTop={isLandscape ? "0rem" : isMobile ? "1rem" : "2.4rem"}
        >
          {config ? (
            <Avatar shape={"circle"} {...config} style={avatarStyle} />
          ) : (
            <CommonAvatar
              bg={profileImageUrl ? "transperent" : "blackAlpha.900"}
              src={profileImageUrl}
              width={isLandscape ? "75px" : isMobile ? "100px" : "200px"}
              height={isLandscape ? "75px" : isMobile ? "100px" : "200px"}
            />
          )}
        </Box>
        <AvatarEditor
          config={config}
          updateConfig={updateConfig}
          isSmallWidth={isMobile}
        />
        <AvatarList
          selectConfig={selectConfig}
          isSmallWidth={isMobile}
          isLandscape={isLandscape}
        />
        <Flex width="100%" marginTop="auto">
          <Button
            width="100%"
            onClick={handleCancelAvatar}
            isDisabled={config === null}
            marginRight="1rem"
            border={"1px solid #e2e8f0"}
            color="#718096"
            background="#ffffff"
            pointerEvents={"auto"}
            minW="1rem"
            _hover={{ color: "black", background: "#ffffff" }}
            _focus={{}}
            _disabled={{ opacity: 0.25 }}
          >
            {t("Remove")}
          </Button>
          <Button
            onClick={handleCloseModal}
            width="100%"
            isDisabled={!config}
            minW="1rem"
            bg={roomStyles?.joinButtonBackgroundColor ?? "#1AC541"}
            border={
              roomStyles?.joinButtonBorderColor
                ? `1px solid ${
                    roomStyles?.joinButtonBorderColor
                      ? roomStyles.joinButtonBorderColor
                      : "transparent"
                  }`
                : "0px"
            }
            textColor={roomStyles?.joinPageButtonTextColor ?? "#FFFFFF"}
            _hover={{
              backgroundColor:
                roomStyles?.joinButtonMouseoverBackgroundColor ?? "#24A840",
              textColor:
                roomStyles?.joinPageButtonMouseoverTextColor ?? "#FFFFFF"
            }}
            _focus={{}}
            _disabled={{ opacity: 0.25 }}
          >
            {t("Use")}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}

export const AvatarsModal = React.memo(_AvatarsModal)
